import React, { createContext, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ThemeProvider } from '@mui/material';
import { MsalProvider } from '@azure/msal-react';
import { EventMessage, EventType } from '@azure/msal-browser';

import { msalInstance } from './services/auth/msalInstance';
import App from './App';
import { customPalette, theme } from './styles/theme';
import { TranslationProvider } from './context/TranslationContext';
import { appInsightsManager } from './services/appInsights/appInsightsManager';
import { useAuthStore, useTranslationsStore } from './stores/storeRoot';
import {
  ApplicationLoaderProvider,
  useLoader,
} from './context/ApplicationLoaderContext';
import ApplicationLoader from './presentation/components/ApplicationLoader/ApplicationLoader';

// eslint-disable-next-line @typescript-eslint/no-var-requires
require('dotenv').config({
  path: `.env.${process.env.NODE_ENV}`,
});

const CustomPaletteContext = createContext(customPalette);

let root: Root | null = null;

// Renders the main application once all necessary initializations are complete
const renderMainApp = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('[renderMainApp] Root element not found!');
    return;
  }

  if (!root) {
    root = createRoot(rootElement); // Initializes the root if not already created
  }

  root.render(
    <React.StrictMode>
      <div key="mainApp">
        <MsalProvider instance={msalInstance}>
          <ApplicationLoaderProvider>
            <ThemeProvider theme={theme}>
              <CustomPaletteContext.Provider value={customPalette}>
                <TranslationProvider>
                  <App />
                </TranslationProvider>
              </CustomPaletteContext.Provider>
            </ThemeProvider>
          </ApplicationLoaderProvider>
        </MsalProvider>
      </div>
    </React.StrictMode>
  );
};

let loginSuccessOccured: boolean = false;

// This function initializes necessary services (MSAL, translations, etc.) and renders the main application
const initializeAppAndRender = async (
  setLoading: (loading: boolean) => void
) => {
  try {
    await msalInstance.initialize();

    msalInstance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_SUCCESS) {
        loginSuccessOccured = true;
      }
      if (message.eventType === EventType.HANDLE_REDIRECT_END) {
        useAuthStore.getState().navigatedUnauthenticated = !loginSuccessOccured;
      }
    });

    // Initialize translations store
    await useTranslationsStore.getState().initialize();

    // Enable application insights tracking
    try {
      appInsightsManager.tryEnableTracking();
    } catch (e) {
      console.error(`Error initializing ai: ${e}`);
    }
  } catch (e) {
    console.error(
      '[initializeAppAndRender] Error during app initialization:',
      e
    );
  } finally {
    setLoading(false); // Loading finished, render the main application
    renderMainApp();
  }
};

// AppInitializer component triggers the initializeAppAndRender process and handles the loading state
const AppInitializer: React.FC = () => {
  const { setLoading } = useLoader();

  useEffect(() => {
    // Start initialization process and manage loading state
    initializeAppAndRender(setLoading);

    return () => {
      setLoading(false);
    };
  }, [setLoading]);

  return <ApplicationLoader />; // Displays the loader while initialization is in progress
};

// Renders the initial loader before the main application starts rendering
const renderInitialLoader = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('[renderInitialLoader] Root element not found!');
    return;
  }

  if (!root) {
    root = createRoot(rootElement); // Create the root if not already initialized
  }

  // Render the ApplicationLoader to show loading state immediately
  root.render(
    <React.StrictMode>
      <div key="initialLoader">
        <ApplicationLoader />
      </div>
    </React.StrictMode>
  );
};

renderInitialLoader(); // Initial call to show the loader

// The setTimeout ensures that the loader is displayed first, while initialization happens in the background.
// Once initialization is complete, the main app is rendered. This technique guarantees that the loader
// appears before anything else, preventing issues with flickering or delayed rendering due to initialization delays.
setTimeout(() => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('[setTimeout] Root element not found!');
    return;
  }

  if (!root) {
    root = createRoot(rootElement); // Ensure the root is created before rendering the application
  }

  root.render(
    <React.StrictMode>
      <div key="appInitializer">
        <MsalProvider instance={msalInstance}>
          <ApplicationLoaderProvider>
            <ThemeProvider theme={theme}>
              <CustomPaletteContext.Provider value={customPalette}>
                <TranslationProvider>
                  <AppInitializer />
                </TranslationProvider>
              </CustomPaletteContext.Provider>
            </ThemeProvider>
          </ApplicationLoaderProvider>
        </MsalProvider>
      </div>
    </React.StrictMode>
  );
}, 500);
